import React from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import landingApi from "../../contexts/landingApiClient";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const EditAffiliateModal = ({ open, handleClose, affiliate, onAffiliateUpdated }) => {
  const { t } = useTranslation();
  if (!affiliate) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-affiliate-modal-title"
    >
      <Box sx={style}>
        <Typography variant="h6" component="h2">
          {t("edit_affiliate")}
        </Typography>
        <Formik
          initialValues={{
            name: affiliate.attributes.name || "",
            email: affiliate.attributes.email || "",
            status: affiliate.attributes.status || "inactive",
            submit: null,
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            name: Yup.string().max(255).required(t("validation_name_required")),
            status: Yup.string().required(t("validation_status_required")),
          })}
          onSubmit={async (
            values,
            { setErrors, setStatus, setSubmitting }
          ) => {
            try {
              const payload = {
                name: values.name,
                status: values.status,
              };
              await landingApi.put(`/affiliates/${affiliate.id}`, {
                data: payload,
              });
              if (onAffiliateUpdated) {
                onAffiliateUpdated();
              }
              handleClose();
            } catch (error) {
              console.error("Failed to update affiliate", error);
              setStatus({ success: false });
              setErrors({ submit: error.message || t("error_update_affiliate_failed") });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box mt={2}>
                <TextField
                  name="name"
                  label={t("name")}
                  fullWidth
                  margin="normal"
                  value={values.name}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <TextField
                  name="email"
                  label={t("email")}
                  fullWidth
                  margin="normal"
                  value={values.email}
                  disabled
                />
                <FormControl
                  fullWidth
                  margin="normal"
                  error={Boolean(touched.status && errors.status)}
                >
                  <InputLabel>{t("status")}</InputLabel>
                  <Select
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="active">{t("active")}</MenuItem>
                    <MenuItem value="inactive">{t("inactive")}</MenuItem>
                    <MenuItem value="disabled">{t("disabled")}</MenuItem>
                  </Select>
                  <FormHelperText>
                    {touched.status && errors.status}
                  </FormHelperText>
                </FormControl>
                {errors.submit && (
                  <Box mt={2}>
                    <Typography color="error">{errors.submit}</Typography>
                  </Box>
                )}
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button onClick={handleClose} sx={{ mr: 1 }}>
                    {t("cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {t("update")}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditAffiliateModal;
